/**
 * On-demand report adapter: one selected jurisdiction → existing GHG calculation
 * → report view model. Does not recalculate a statewide inventory.
 */

import { supabaseClient } from '../../services/supabase';
import {
  fetchGHGInventoryData,
  type GHGInventoryResult,
} from '../../services/ghgInventoryCalculationService';
import { attachEvidenceToSectors } from './attachInventoryEvidence';
import { FROZEN_UNIVERSE, buildGHGInventoryReport } from './buildGHGInventoryReport';
import { buildConsumptionInventoryFromCbei, type CbeiRawRow } from './cbeiReportScaling';
import { resolveByGeofips, toReportJurisdiction } from './crosswalk';
import { REPLICA_COMPARISON_COPY } from './memberLanguage';
import {
  mapLiveInventoryTotal,
  mapLiveInventoryYear,
  mapLiveResultToHistoricalPoints,
  mapLiveResultToSectorObservations,
} from './mapLiveInventoryResult';
import { reconcileTransportActivity } from './transportationActivity';
import type { BuildGHGInventoryReportInput, GeneratedGHGInventoryReport } from './types';
import { numOrNull, reportValue } from './valueHelpers';
import {
  applyStoredOrScaledWastewater,
  wastewaterGeofipsKeys,
  type StoredWastewaterObservation,
} from './wastewaterSource';

export interface InventoryReportAdapter {
  loadReport(geofips: number, year?: number): Promise<GeneratedGHGInventoryReport>;
}

export type FetchGhgInventoryFn = (location: string) => Promise<GHGInventoryResult>;

let lastRequestedGeofips: number[] = [];
let lastReportQueryTables: string[] = [];

export function resetReportLoadTracking(): void {
  lastRequestedGeofips = [];
  lastReportQueryTables = [];
}

export function getLastReportLoadGeofips(): number[] {
  return [...lastRequestedGeofips];
}

export function getLastReportQueryTables(): string[] {
  return [...lastReportQueryTables];
}

function trackQuery(table: string): void {
  lastReportQueryTables.push(table);
}

export function locationNameForReport(geofips: number): string {
  const crosswalk = resolveByGeofips(geofips);
  if (!crosswalk) throw new Error(`No crosswalk entry for geofips ${geofips}`);
  return crosswalk.populationName || crosswalk.capName;
}

function locationSlugForPolicyTool(name: string): string {
  return name
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/--+/g, '-');
}

async function fetchPopulationRow(geofips: number, year: number) {
  trackQuery('population');
  const { data, error } = await supabaseClient
    .from('population')
    .select('population,location_name,county,geography_type')
    .eq('geofips', geofips)
    .eq('year', year)
    .order('geofips', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data;
}

async function fetchDensityRow(geofips: number, year: number) {
  trackQuery('population_density');
  const { data, error } = await supabaseClient
    .from('population_density')
    .select('population_density,land_area_sq_mi,year')
    .eq('geofips', geofips)
    .eq('year', year)
    .order('year', { ascending: false })
    .order('geofips', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data;
}

async function fetchCcasiRow(geofips: number) {
  trackQuery('ccas');
  const { data, error } = await supabaseClient
    .from('ccas')
    .select('geofips,load_serving_entity,previous_iou,cca_start_year')
    .eq('geofips', geofips)
    .order('geofips', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data;
}

async function fetchCbeiRows(geofips: number): Promise<CbeiRawRow[]> {
  trackQuery('cbei_cities');
  const { data, error } = await supabaseClient
    .from('cbei_cities')
    .select(
      'year,transportation_total_emissions,housing_total_emissions,food_total_emissions,goods_total_emissions,services_total_emissions,hhcount'
    )
    .eq('geofips', geofips)
    .order('year', { ascending: true })
    .order('geofips', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as CbeiRawRow[];
}

async function fetchJobsRow(geofips: number, year: number) {
  trackQuery('lodes_employees_workplace');
  const placeCode = String(geofips).slice(-6);
  const { data, error } = await supabaseClient
    .from('lodes_employees_workplace')
    .select('year,total_worker')
    .eq('geofips', placeCode)
    .eq('geography_type', 'place')
    .eq('year', year)
    .order('year', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) return null;
  return data;
}

async function fetchHousingRow(geofips: number, year: number) {
  trackQuery('housing_to_employees');
  const { data, error } = await supabaseClient
    .from('housing_to_employees')
    .select('year,housing_units')
    .eq('geofips', geofips)
    .eq('geography_type', 'place')
    .eq('year', year)
    .order('year', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) return null;
  return data;
}

async function fetchLatestWastewaterSummary(
  geofips: number,
  year: number
): Promise<StoredWastewaterObservation | null> {
  trackQuery('v_wastewater_inventory_summary');
  const { data, error } = await supabaseClient
    .from('v_wastewater_inventory_summary')
    .select(
      'geofips,inventory_year,jurisdiction_name,wastewater_emissions_mtco2e,methodology_version,ef_version,coverage_status'
    )
    .lte('inventory_year', year)
    .in('geofips', wastewaterGeofipsKeys(geofips))
    .order('inventory_year', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error || !data) return null;
  const mt = numOrNull((data as { wastewater_emissions_mtco2e?: number }).wastewater_emissions_mtco2e);
  if (mt == null) return null;
  return {
    geofips: String((data as { geofips?: string }).geofips ?? geofips),
    inventoryYear: Number((data as { inventory_year?: number }).inventory_year ?? year),
    mtCo2e: mt,
    methodologyVersion: (data as { methodology_version?: string | null }).methodology_version ?? null,
    efVersion: (data as { ef_version?: string | null }).ef_version ?? null,
    coverageStatus: (data as { coverage_status?: string | null }).coverage_status ?? null,
  };
}

function formatUtilityProvider(
  ccas: { load_serving_entity?: string | null; previous_iou?: string | null; cca_start_year?: number | null } | null,
  year: number
): string | null {
  if (!ccas) return null;
  const lse = ccas.load_serving_entity;
  const prev = ccas.previous_iou;
  const start = ccas.cca_start_year;
  if (start != null && year < start && prev) return `${prev} (IOU before CCA start ${start})`;
  if (lse) return String(lse);
  if (prev) return String(prev);
  return null;
}

export async function buildLiveReportInput(
  geofips: number,
  year = 2023,
  deps?: { fetchInventory?: FetchGhgInventoryFn }
): Promise<BuildGHGInventoryReportInput> {
  const crosswalk = resolveByGeofips(geofips);
  if (!crosswalk) throw new Error(`No crosswalk entry for geofips ${geofips}`);

  lastRequestedGeofips = [geofips];
  lastReportQueryTables = [];

  const location = locationNameForReport(geofips);
  const fetchInventory = deps?.fetchInventory ?? fetchGHGInventoryData;

  const [inventory, population, density, ccas, cbei, jobsRow, housingRow, wastewaterStored] = await Promise.all([
    fetchInventory(location).then((result) => {
      trackQuery('fetchGHGInventoryData');
      return result;
    }),
    fetchPopulationRow(geofips, year),
    fetchDensityRow(geofips, year),
    fetchCcasiRow(geofips),
    fetchCbeiRows(geofips),
    fetchJobsRow(geofips, year),
    fetchHousingRow(geofips, year),
    fetchLatestWastewaterSummary(geofips, year),
  ]);

  const inventoryYear = mapLiveInventoryYear(inventory, year);
  const totalMt = mapLiveInventoryTotal(inventory, inventoryYear);
  const evidence = inventory.inventoryEvidence;
  const wwYear = wastewaterStored?.inventoryYear ?? null;
  let basePopulation = population;
  let baseJobsRow = jobsRow;
  if (wwYear != null && wwYear !== inventoryYear) {
    const [basePop, baseJobs] = await Promise.all([
      fetchPopulationRow(geofips, wwYear),
      fetchJobsRow(geofips, wwYear),
    ]);
    basePopulation = basePop;
    baseJobsRow = baseJobs;
  }
  const sectors = applyStoredOrScaledWastewater(
    attachEvidenceToSectors(
      mapLiveResultToSectorObservations(inventory, inventoryYear),
      evidence,
      inventoryYear
    ),
    wastewaterStored,
    inventoryYear,
    {
      inventoryPopulation: numOrNull(population?.population),
      inventoryWorkers: numOrNull((jobsRow as { total_worker?: number } | null)?.total_worker),
      basePopulation: numOrNull(basePopulation?.population),
      baseWorkers: numOrNull((baseJobsRow as { total_worker?: number } | null)?.total_worker),
    }
  );
  const historicalPoints = mapLiveResultToHistoricalPoints(inventory);

  const pop = numOrNull(population?.population);
  const den = numOrNull(density?.population_density);
  const land = numOrNull((density as { land_area_sq_mi?: number } | null)?.land_area_sq_mi);
  const jobs = numOrNull((jobsRow as { total_worker?: number } | null)?.total_worker);
  const housing = numOrNull((housingRow as { housing_units?: number } | null)?.housing_units);
  const cbeiYear = cbei.find((r) => r.year === inventoryYear);
  const hh = numOrNull(cbeiYear?.hhcount);
  const utility = formatUtilityProvider(ccas, inventoryYear);
  const jurisdiction = toReportJurisdiction(crosswalk);
  const consumptionHistory = cbei.map((row) => {
    const inv = buildConsumptionInventoryFromCbei([row], row.year);
    const total = inv.totalMt.value;
    const households = inv.householdCount.value;
    return {
      year: row.year,
      totalMt: total,
      perHouseholdMt: total != null && households != null && households > 0 ? total / households : null,
    };
  });

  return {
    jurisdiction,
    universe: FROZEN_UNIVERSE,
    inventoryRun: {
      inventoryRunId: `live:${geofips}:${inventoryYear}`,
      inventoryYear,
      methodLabel: 'ClimatePlans GHG inventory (current software calculation)',
      methodVersion: null,
      liveCalculation: {
        kind: 'live_ghg_inventory',
        functionName: 'fetchGHGInventoryData',
        location,
        geofips,
        year: inventoryYear,
      },
      runStatus: 'live_calculation',
      publicationStatus: 'provisional',
    },
    inventorySource: 'live_calculation',
    inventoryTotalMt: totalMt,
    storedTotalMt: totalMt,
    rankingAvailability: 'unavailable',
    rankingPool: [],
    populationForYear: reportValue(pop, 'residents', pop == null ? 'missing' : 'observed', {
      sourceTable: 'population',
      sourceColumn: 'population',
      sourceYear: inventoryYear,
    }),
    households:
      hh != null
        ? reportValue(hh, 'households', 'modeled', {
            sourceTable: 'cbei_cities',
            sourceColumn: 'hhcount',
            sourceYear: inventoryYear,
            calculationRule: 'CBEI model household count; not a Census ACS observation',
          })
        : reportValue(null, 'households', 'missing', {
            sourceTable: 'cbei_cities',
            sourceColumn: 'hhcount',
            sourceYear: inventoryYear,
          }),
    populationDensity:
      den != null
        ? reportValue(den, 'residents/sq mi', 'observed', {
            sourceTable: 'population_density',
            sourceColumn: 'population_density',
            sourceYear: inventoryYear,
          })
        : undefined,
    landAreaSqMi:
      land != null
        ? reportValue(land, 'sq mi', 'observed', {
            sourceTable: 'population_density',
            sourceColumn: 'land_area_sq_mi',
            sourceYear: inventoryYear,
          })
        : undefined,
    utilityProvider: utility
      ? reportValue(utility, '', 'modeled', {
          sourceTable: 'ccas',
          sourceColumn: 'load_serving_entity,previous_iou,cca_start_year',
          sourceYear: inventoryYear,
          calculationRule:
            'ccas row is not year-specific; treated as applicable for the inventory year, not observed in that year',
        })
      : undefined,
    jobs:
      jobs != null
        ? reportValue(jobs, 'jobs', 'observed', {
            sourceTable: 'lodes_employees_workplace',
            sourceColumn: 'total_worker',
            sourceYear: inventoryYear,
          })
        : reportValue(null, 'jobs', 'missing', {
            sourceTable: 'lodes_employees_workplace',
            sourceColumn: 'total_worker',
            sourceYear: inventoryYear,
          }),
    housingUnits:
      housing != null
        ? reportValue(housing, 'housing units', 'observed', {
            sourceTable: 'housing_to_employees',
            sourceColumn: 'housing_units',
            sourceYear: inventoryYear,
          })
        : reportValue(null, 'housing units', 'missing', {
            sourceTable: 'housing_to_employees',
            sourceColumn: 'housing_units',
            sourceYear: inventoryYear,
          }),
    sectorObservations: sectors,
    historicalPoints,
    consumption: buildConsumptionInventoryFromCbei(cbei, inventoryYear),
    consumptionHistory,
    activityEvidence: evidence ?? null,
    replicaDisclosure: evidence
      ? {
          methodVersion: evidence.replicaMethodVersion,
          attribution: evidence.replicaAttribution,
          comparisonNote: REPLICA_COMPARISON_COPY,
        }
      : {
          methodVersion: null,
          attribution: REPLICA_COMPARISON_COPY,
          comparisonNote: REPLICA_COMPARISON_COPY,
        },
    scenarioHandoff: {
      href: `/${locationSlugForPolicyTool(location)}/policy-tool`,
      inventoryVsScenario:
        'The inventory estimates historical community emissions. The existing ClimatePlans scenario tool explores future policy pathways. Those are different products and must not be read as the same calculation.',
      levers: [
        'Building electrification (existing ClimatePlans scenario tool)',
        'Vehicle electrification (existing ClimatePlans scenario tool)',
      ],
    },
    tractConsumption: {
      omitted: true,
      omissionNote:
        'A tract-level consumption map is omitted. Tract rows are not queried until a print-safe city choropleth is rendered.',
    },
    transportDiagnostics: (() => {
      const hdv = sectors.find((s) => s.sectorCode === 'hdv');
      const idx = evidence?.years.indexOf(inventoryYear) ?? -1;
      return {
        hdv: reconcileTransportActivity({
          sectorCode: 'hdv',
          emissionsMt: hdv?.emissionsMt.value ?? null,
          displayedActivity: hdv?.activity?.value ?? null,
          activityUnit: hdv?.activityUnit ?? 'miles',
          activitySource: 'emfac2025_fleet.total_vmt',
          emissionsSource:
            'emfac2025_fleet class HDV identity, then YoY emissions smoothing when change exceeds 50%, then 2005–2014 county backcast',
          smoothingApplied: idx >= 0 ? evidence?.hdvSmoothingApplied?.[idx] ?? null : null,
          activityRole: idx >= 0 ? evidence?.hdvActivityRole?.[idx] ?? null : null,
        }),
        ldv: reconcileTransportActivity({
          sectorCode: 'ldv',
          emissionsMt: sectors.find((s) => s.sectorCode === 'ldv')?.emissionsMt.value ?? null,
          displayedActivity: sectors.find((s) => s.sectorCode === 'ldv')?.activity?.value ?? null,
          activityUnit: 'miles',
          activitySource: 'ClimatePlans combined residential LDV VMT used by fetchGHGInventoryData',
          emissionsSource: 'LDV VMT × EMFAC emission factor in fetchGHGInventoryData',
          smoothingApplied: false,
          activityRole: 'calculation_input',
        }),
      };
    })(),
  };
}

export const liveInventoryReportAdapter: InventoryReportAdapter = {
  async loadReport(geofips: number, year = 2023) {
    const input = await buildLiveReportInput(geofips, year);
    return buildGHGInventoryReport(input);
  },
};
